import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { append } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { useAnalytics } from '~/hooks/useAnalytics'
import {
  useBlockNumberQuery,
  useGetBlockedNumbersQuery,
  useUnblockNumberQuery,
} from '~/queries/blockNumberQueries'
import Logger from '~/services/Logger'
import { AnalyticsNamespace } from '~/types/Analytics'
import { SharedAnalyticsEvents } from '~/types/Analytics/SharedAnalyticsEvents'
import { formatPhoneNumberForRegion, toE164 } from '~/utils/formatter'
import { showErrorToast } from '~/utils/toast'

export interface BlockedNumber {
  phoneNumber: string
  id: number
}

interface BlockedNumbersStorage {
  blocked: BlockedNumber[] | null | undefined
}

const blockedNumbersAtom = atomWithStorage<BlockedNumbersStorage>(
  StorageKeys.BlockedNumbers,
  {
    blocked: null,
  },
)

/**
 * Clean up a phone number by removing all non-numeric characters and the US Country code (if found)
 * This helps with comparing if a phone number is blocked when it is returned formatted differently for some APIs.
 * For example: sometimes it is returned 8015551234 and other times as +18015551234
 */
export function formatPhoneNumberForBlocking(phoneNumber: string): string {
  // remove the US country code if it is at the start
  let cleanedNumber = phoneNumber.replace(/^\+1/, '')
  // remove all non-digit characters
  cleanedNumber = cleanedNumber.replace(/\D+/g, '')
  // remove leading '1' if the number has 11 digits
  if (cleanedNumber.length === 11 && cleanedNumber.startsWith('1')) {
    cleanedNumber = cleanedNumber.substring(1)
  }

  return cleanedNumber
}

/**
 * Normalizes a number for blocking and for comparing against blocked numbers.
 * Extensions and short codes (6 or fewer digits, no `+`) stay as digits;
 * everything else becomes E.164. A number that isn't valid locally but has
 * 8-20 digits gets a `+`, matching how FreeSWITCH normalizes caller IDs
 * (`get_phone_number_as_e164` in freeswitch-scripts), so the stored value
 * matches the key checked during a call. Anything else stays as digits.
 */
export function normalizeBlockedNumber(
  phoneNumber: string | null | undefined,
): string {
  if (!phoneNumber) return ''
  const trimmed = phoneNumber.trim()
  const hasPlusPrefix = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D+/g, '')
  if (!hasPlusPrefix && digits.length <= 6) return digits
  const e164 = toE164(trimmed)
  if (e164) return e164
  return hasPlusPrefix || /^[1-9]\d{7,19}$/.test(digits) ? `+${digits}` : digits
}

const countDigits = (phoneNumber: string) =>
  phoneNumber.replace(/\D+/g, '').length

/** Returns true if any of the numbers supplied can be blocked and false if none can be blocked or no number was supplied */
export const useCanBlockNumber = (
  phoneNumber: string | string[] | null | undefined,
) => {
  return useMemo(() => {
    if (!phoneNumber) {
      return false
    }
    const numbers =
      typeof phoneNumber === 'string' ? [phoneNumber] : phoneNumber
    return numbers.some(
      (number) => countDigits(normalizeBlockedNumber(number)) >= 5,
    )
  }, [phoneNumber])
}

const blockedNumberIndexes = new WeakMap<
  BlockedNumber[],
  Map<string, BlockedNumber>
>()

/** Normalized lookup for a blocked-numbers list, built once per list instance. */
const getBlockedNumberIndex = (blockedNumbers: BlockedNumber[]) => {
  let index = blockedNumberIndexes.get(blockedNumbers)
  if (!index) {
    index = new Map()
    for (const blocked of blockedNumbers) {
      const key = normalizeBlockedNumber(blocked.phoneNumber)
      if (!index.has(key)) index.set(key, blocked)
    }
    blockedNumberIndexes.set(blockedNumbers, index)
  }
  return index
}

/** Finds the blocked entry matching a number in any format. */
export const findBlockedNumber = (
  blockedNumbers: BlockedNumber[] | null | undefined,
  phoneNumber: string | null | undefined,
): BlockedNumber | undefined => {
  const normalizedPhoneNumber = normalizeBlockedNumber(phoneNumber)
  if (!blockedNumbers || !normalizedPhoneNumber) {
    return undefined
  }
  return getBlockedNumberIndex(blockedNumbers).get(normalizedPhoneNumber)
}

export const useFindBlockedNumber = (
  blockedNumbers?: BlockedNumber[],
  phoneNumber?: string | null,
) => {
  return useMemo(
    () => findBlockedNumber(blockedNumbers, phoneNumber),
    [blockedNumbers, phoneNumber],
  )
}

const logger = new Logger('BlockedNumbers')
let lastLoadedBlockedNumbers = 0

interface UnblockNumberOptions {
  /** Called once the number is no longer blocked. */
  onUnblocked?: () => void
}

interface useBlockedNumbersProps {
  forceRefresh?: boolean
}

export const useBlockedNumbers = ({
  forceRefresh,
}: useBlockedNumbersProps = {}) => {
  //added force refresh prop
  const { pbxId } = useUserState()
  const [blockedNumbers, setBlockedNumbers] = useAtom(blockedNumbersAtom)
  const { track } = useAnalytics(AnalyticsNamespace.SHARED)
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const { t } = useTranslation()

  const pendingUnblock = useRef<{
    phoneNumber: string
    options: UnblockNumberOptions
  }>(undefined)
  const [isUnblocking, setIsUnblocking] = useState(false)
  const [isBlocking, setIsBlocking] = useState(false)

  const { refetch: refetchBlockedNumbers } = useGetBlockedNumbersQuery({
    pbxId,
    onSuccess: (prevPbxId, apiBlockedNumbers) => {
      if (pbxId !== prevPbxId) return

      const blockedNumbers = apiBlockedNumbers.map((blockedNumber) => ({
        phoneNumber: normalizeBlockedNumber(blockedNumber.phone_number.number),
        id: blockedNumber.id,
      }))
      setBlockedNumbers({ blocked: blockedNumbers })
    },
    onError: (error) => {
      // Ensure we pass an Error instance to logger
      const errorToLog =
        error instanceof Error ? error : error ? new Error(String(error)) : null
      logger.error('Failed to get blocked numbers', errorToLog)
    },
  })

  const reset = () => {
    setBlockedNumbers({ blocked: null })
    lastLoadedBlockedNumbers = 0
  }

  // #region Unblock number
  const unblockNumberQuery = useUnblockNumberQuery()

  const unblockNumber = (
    blockedNumberId: number,
    phoneNumber: string,
    options: UnblockNumberOptions = {},
  ) => {
    track(SharedAnalyticsEvents.UnblockNumber)

    const formattedPhoneNumber = formatPhoneNumberForRegion(phoneNumber)

    setIsUnblocking(true)
    unblockNumberQuery(blockedNumberId, {
      onSuccess: (unblocked) => {
        logger.info(`Unblocked ${formattedPhoneNumber} ${unblocked}`)
        if (!unblocked) {
          logger.info(
            `Reloading blocked numbers, got 404 for a known blocked number id`,
          )
          refetchBlockedNumbers().then(() => {
            pendingUnblock.current = { phoneNumber, options }
          })
        } else {
          setBlockedNumbers((prev) => {
            return {
              blocked: prev.blocked?.filter(
                (blocked) => blocked.id !== blockedNumberId,
              ),
            }
          })
          options.onUnblocked?.()
        }
      },
      onError: () => {
        logger.error(`Failed to unblock number ${formattedPhoneNumber}`)
        showErrorToast({
          message: t('confirmBlockNumberModal.toastUnblockError', {
            formattedPhoneNumber,
          }),
        })
      },
      onCompletion: () => {
        setIsUnblocking(false)
      },
    })
  }

  const blockNumberQuery = useBlockNumberQuery({ pbxId })

  const blockNumber = (phoneNumber: string) => {
    const normalizedPhoneNumber = normalizeBlockedNumber(phoneNumber)
    const formattedPhoneNumber = formatPhoneNumberForRegion(
      normalizedPhoneNumber,
    )
    track(SharedAnalyticsEvents.BlockNumber)
    setIsBlocking(true)
    blockNumberQuery(normalizedPhoneNumber, {
      onError: () => {
        showErrorToast({
          message: t('confirmBlockNumberModal.toastBlockError', {
            formattedPhoneNumber,
          }),
        })
      },
      onSuccess: (blockedNumber) => {
        if (!blockedNumber) {
          // Someone else blocked the number, we need to reload the blocked numbers to get the new one
          refetchBlockedNumbers()
        } else {
          setBlockedNumbers((prev) => {
            return {
              blocked: append(
                {
                  phoneNumber: normalizeBlockedNumber(
                    blockedNumber.phone_number,
                  ),
                  id: blockedNumber.id,
                },
                prev.blocked ?? [],
              ),
            }
          })
        }
      },
      onCompletion: () => {
        setIsBlocking(false)
      },
    })
  }

  const blockNumberWithConfirmation = (phoneNumber: string) => {
    const formattedPhoneNumber = formatPhoneNumberForRegion(
      normalizeBlockedNumber(phoneNumber),
    )
    setConfirmModalState({
      title: t('confirmBlockNumberModal.title', { formattedPhoneNumber }),
      message: t('confirmBlockNumberModal.message'),
      confirmText: t('confirmBlockNumberModal.confirmText'),
      cancelText: t('confirmBlockNumberModal.cancelText'),
      closeAfterConfirm: true,
      isDestructive: true,
      onConfirm: () => {
        blockNumber(phoneNumber)
      },
    })
  }

  useEffect(() => {
    // There was a problem while unblocking a number (someone unblocked and reblocked it, making the id invalid)
    // so we watch for the reload to finish and unblock the number once it finishes reloading the full list
    const pending = pendingUnblock.current
    if (!pending) {
      return
    }

    logger.info(`Unblocking pending ${pending.phoneNumber}`)
    const blockedNumberToUnblock = findBlockedNumber(
      blockedNumbers.blocked,
      pending.phoneNumber,
    )
    // We only want to check one time, so we reset the pending unblock here
    pendingUnblock.current = undefined

    if (!blockedNumberToUnblock) {
      logger.info('Unblocking pending found number is already unblocked')
      pending.options.onUnblocked?.()
      return
    }

    logger.info(`Unblock pending found new number to unblock`)
    unblockNumber(
      blockedNumberToUnblock.id,
      blockedNumberToUnblock.phoneNumber,
      pending.options,
    )
  }, [blockedNumbers])

  useEffect(() => {
    // The first time someone calls this hook, reload the blocked numbers
    const now = new Date().getTime()
    const timeSinceLastLoaded = now - lastLoadedBlockedNumbers
    // Only load once per day. Eventually it would be nice to get this like we do messages instead of polling like this
    if (pbxId && (forceRefresh || timeSinceLastLoaded > 1000 * 60 * 60 * 24)) {
      lastLoadedBlockedNumbers = now
      refetchBlockedNumbers()
    }
  }, [forceRefresh])

  return {
    blockedNumbers: blockedNumbers.blocked ?? undefined,
    isUnblocking,
    isBlocking,
    hasLoadedBlockedNumbers: blockedNumbers.blocked !== null,
    setBlockedNumbers: (blockedNumbers: BlockedNumber[]) => {
      setBlockedNumbers({ blocked: blockedNumbers })
    },
    refetchBlockedNumbers,
    unblockNumber,
    blockNumber,
    blockNumberWithConfirmation,
    reset,
  }
}
